import React, { useState } from "react";
import { PlayIcon, PauseIcon, StopIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";

/**
 * ControlPanel component for controlling the streaming from the UI.
 */
export function ControlPanel({ serverState, onSendCommand }) {
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    delay_ms: 200,  // Default: 200ms - good pace for demos
    window_size: 10,
    ai_enabled: true,
    max_cycles: 20,  // Limit cycles per engine (must be >= window_size)
    max_engines: 5, // Limit number of engines (1-100)
  });

  // Validation: max_cycles must be >= window_size for features to compute
  const isConfigValid = settings.max_cycles >= settings.window_size;

  const isStreaming = serverState?.streaming || false;
  const isPaused = serverState?.paused || false;
  const eventsSent = serverState?.events_sent || 0;
  const totalEvents = serverState?.total_events || 0;
  const progress = totalEvents > 0 ? (eventsSent / totalEvents) * 100 : 0;

  const handleStart = () => {
    onSendCommand({
      action: "start",
      delay_ms: settings.delay_ms,
      window_size: settings.window_size,
      ai_enabled: settings.ai_enabled,
      max_cycles: settings.max_cycles,
      max_engines: settings.max_engines,
    });
  };

  const handleStop = () => {
    onSendCommand({ action: "stop" });
  };

  const handlePauseResume = () => {
    onSendCommand({ action: isPaused ? "resume" : "pause" });
  };

  const handleDelayChange = (newDelay) => {
    setSettings((prev) => ({ ...prev, delay_ms: newDelay }));
    if (isStreaming) {
      onSendCommand({ action: "set_delay", delay_ms: newDelay });
    }
  };

  return (
    <div className="control-panel">
      <div className="control-header">
        <h3>Stream Control</h3>
        <button
          className="settings-btn"
          onClick={() => setShowSettings(!showSettings)}
          title="Settings"
        >
          <Cog6ToothIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Config Warning */}
      {!isConfigValid && !isStreaming && (
        <div className="config-warning">
          ⚠️ Max cycles ({settings.max_cycles}) must be ≥ window size ({settings.window_size})
        </div>
      )}

      {/* Main Controls */}
      <div className="control-buttons">
        {!isStreaming ? (
          <button
            className="control-btn start"
            onClick={handleStart}
            disabled={!isConfigValid}
            title={!isConfigValid ? "Fix config: max_cycles must be >= window_size" : ""}
          >
            <PlayIcon className="h-5 w-5" />
            Start Streaming
          </button>
        ) : (
          <>
            <button className="control-btn pause" onClick={handlePauseResume}>
              {isPaused ? <PlayIcon className="h-5 w-5" /> : <PauseIcon className="h-5 w-5" />}
              {isPaused ? "Resume" : "Pause"}
            </button>
            <button className="control-btn stop" onClick={handleStop}>
              <StopIcon className="h-5 w-5" />
              Stop
            </button>
          </>
        )}
      </div>

      {/* Progress Bar */}
      {isStreaming && (
        <div className="progress-section">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-text">
            {eventsSent.toLocaleString()} / {totalEvents.toLocaleString()} events
            ({progress.toFixed(1)}%)
          </div>
        </div>
      )}

      {/* Speed Control */}
      {isStreaming && (
        <div className="speed-control">
          <label>Speed:</label>
          <div className="speed-buttons">
            <button
              className={settings.delay_ms >= 1000 ? "active" : ""}
              onClick={() => handleDelayChange(1000)}
              title="1 event per second - great for presentations"
            >
              Present
            </button>
            <button
              className={settings.delay_ms >= 500 && settings.delay_ms < 1000 ? "active" : ""}
              onClick={() => handleDelayChange(500)}
              title="2 events per second - good for demos"
            >
              Demo
            </button>
            <button
              className={settings.delay_ms >= 200 && settings.delay_ms < 500 ? "active" : ""}
              onClick={() => handleDelayChange(200)}
              title="5 events per second - default pace"
            >
              Normal
            </button>
            <button
              className={settings.delay_ms < 200 ? "active" : ""}
              onClick={() => handleDelayChange(50)}
              title="20 events per second - faster simulation"
            >
              Fast
            </button>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && !isStreaming && (
        <div className="settings-panel">
          <div className="setting-row">
            <label>Max Engines:</label>
            <input
              type="number"
              value={settings.max_engines}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  max_engines: parseInt(e.target.value) || 1,
                }))
              }
              min={1}
              max={100}
            />
          </div>
          <div className="setting-row">
            <label>Max Cycles/Engine:</label>
            <input
              type="number"
              value={settings.max_cycles}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  max_cycles: parseInt(e.target.value) || 10,
                }))
              }
              min={10}
              max={362}
            />
          </div>
          <div className="setting-row">
            <label>Event Delay (ms):</label>
            <input
              type="number"
              value={settings.delay_ms}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  delay_ms: parseInt(e.target.value) || 50,
                }))
              }
              min={1}
              max={1000}
            />
          </div>
          <div className="setting-row">
            <label>Window Size:</label>
            <input
              type="number"
              value={settings.window_size}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  window_size: parseInt(e.target.value) || 30,
                }))
              }
              min={5}
              max={100}
            />
          </div>
          <div className="setting-row">
            <label>AI Inference:</label>
            <button
              className={`toggle-btn ${settings.ai_enabled ? "active" : ""}`}
              onClick={() =>
                setSettings((prev) => ({
                  ...prev,
                  ai_enabled: !prev.ai_enabled,
                }))
              }
            >
              {settings.ai_enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
        </div>
      )}

      {/* Status */}
      <div className="stream-status">
        {isStreaming ? (
          isPaused ? (
            <span className="status paused">Paused</span>
          ) : (
            <span className="status active">Streaming...</span>
          )
        ) : (
          <span className="status idle">Ready to stream</span>
        )}
      </div>
    </div>
  );
}
